/**
 * ONE DOOR for admitting characters into a session.
 *
 * There are three ways in - a bulk load, the spawn dialog's count, and
 * staging into a waiting room - and each learned every rule separately:
 * instance numbering (v8.35.0), importing into the session library
 * (v8.26.0 for staging, v8.37.0 for loading) and recording a recent load
 * (v8.37.0). The same three-line change was written into three functions
 * twice in one night, and each time one door was fixed first and the
 * others followed a report later.
 *
 * So this is the rule, once. It answers the two questions every door
 * has to ask and neither of which is about what it builds afterwards:
 *
 *   WHAT DO I CALL THEM?  - numbered against the whole session, roster
 *                           plus every room. See `naming.ts`.
 *   WHO ARE THEY, really? - three of somebody are three arrivals and
 *                           ONE library entry.
 *
 * Deliberately NOT a place that knows about tabs, combatants, rooms or
 * Firestore. What each door builds from an arrival is its own business;
 * the point is that none of them gets to disagree about the name.
 */
import type { Character } from '../character/types';
import { nameNewInstances, type Instance } from './naming';

/** One body arriving, and what it should be called. */
interface Arrival {
  character: Character;
  name: string;
}

export interface Admission {
  /**
   * One per BODY, in the order given. Three of the same character are
   * three arrivals carrying three different names.
   */
  arrivals: Arrival[];
  /**
   * One per DISTINCT character. Three of somebody are one library entry
   * and one line in "Recently Loaded" - the list is of characters, not
   * of combatants.
   */
  distinct: Character[];
  /** How many bodies. `arrivals.length`, named so callers read better. */
  count: number;
}

/** First occurrence wins, so the caller's order survives. */
function uniqueById(characters: readonly Character[]): Character[] {
  const seen = new Set<string>();
  return characters.filter((c) => {
    if (!c?.id || seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

/**
 * Work out what a batch of arrivals should be called, and who is in it.
 *
 * `census` is everybody who already exists - `sessionInstances(roster,
 * rooms)`. Passing it in rather than deriving it keeps this pure and
 * lets a caller take the census BEFORE it mutates anything, which is the
 * order staging needs.
 */
export function admitCharacters(census: readonly Instance[], characters: readonly Character[]): Admission {
  const incoming = (characters ?? []).filter(Boolean);
  const names = nameNewInstances(census, incoming);
  return {
    arrivals: incoming.map((character, i) => ({ character, name: names[i] })),
    distinct: uniqueById(incoming),
    count: incoming.length,
  };
}
